// 站点树扁平化与虚拟视口计算工具函数（纯函数，无DOM依赖）

/**
 * 将 Jeecg / 监测站点树打平为一维数组
 * @param {Array} tree 原始树数据
 * @returns {Array} 扁平化节点数组
 */
export function flattenSiteTree(tree) {
  if (!Array.isArray(tree)) return []
  const nodes = []
  const rex = /^[a-zA-Z]/

  const visit = (items, parentId, level) => {
    for (const item of items) {
      const children = Array.isArray(item.children) ? item.children : []
      const key = String(item.key == null ? '' : item.key)
      const hasChildren = children.length > 0
      const isSite = Boolean(key && !rex.test(key[0]))
      const label = item.title || item.label || ''
      const parent = parentId === null ? null : nodes[parentId]
      const path = parent ? [parent.path, parent.label].filter(Boolean).join(' / ') : ''

      const node = {
        id: nodes.length,
        parentId,
        level,
        key,
        label,
        st: (item.data && item.data.st) || '',
        disabled: Boolean(item.disabled || item.selectable === false),
        hasChildren,
        isLeaf: Boolean(item.isLeaf || !hasChildren),
        isSite,
        online: Boolean(item.online),
        onlineNum: Number(item.onlineNum || 0),
        total: Number(item.total || 0),
        currentStandard: (item.data && item.data.currentStandard) || null,
        upStandard: item.data && item.data.upStandard != null ? String(item.data.upStandard) : null,
        lastTime: (item.data && item.data.lastTime) || '',
        data: item.data || {},
        path,
        searchText: `${label} ${path} ${item.data && item.data.mn ? item.data.mn : ''}`.toLowerCase(),
      }

      nodes.push(node)
      if (hasChildren) {
        visit(children, node.id, level + 1)
      }
    }
  }

  visit(tree, null, 0)
  return nodes
}

/**
 * 包装视图渲染行对象，并进行搜索高亮切词
 * @param {Object} node 扁平节点
 * @param {String} keyword 搜索关键字
 * @returns {Object} 供视图渲染的行对象
 */
export function sitePickerRow(node, keyword = '') {
  if (!node) return null
  const words = String(keyword || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  const escaped = words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const parts = []

  if (escaped.length) {
    const pattern = new RegExp(escaped.join('|'), 'ig')
    let start = 0
    let match
    while ((match = pattern.exec(node.label))) {
      if (match.index > start) {
        parts.push({ text: node.label.slice(start, match.index), hit: false })
      }
      parts.push({ text: match[0], hit: true })
      start = pattern.lastIndex
    }
    if (start < node.label.length) {
      parts.push({ text: node.label.slice(start), hit: false })
    }
  }

  if (!parts.length) {
    parts.push({ text: node.label, hit: false })
  }

  return {
    id: node.id,
    key: node.key,
    label: node.label,
    path: node.path,
    isSite: node.isSite,
    disabled: node.disabled,
    st: node.st,
    level: node.level,
    hasChildren: node.hasChildren,
    isLeaf: node.isLeaf,
    online: node.online,
    onlineNum: node.onlineNum,
    total: node.total,
    currentStandard: node.currentStandard,
    upStandard: node.upStandard,
    lastTime: node.lastTime,
    data: node.data,
    parentId: node.parentId,
    parts,
  }
}

/**
 * 获取当前应该展示的行 ID 列表
 * @param {Array} nodes 扁平节点集合
 * @param {Array|Set} collapsedIds 折叠节点 ID 列表
 * @param {String} keyword 搜索关键字
 * @returns {Array} 可视行节点 ID 数组
 */
export function getSiteTreeIds(nodes, collapsedIds, keyword = '') {
  if (!Array.isArray(nodes) || !nodes.length) return []
  const search = String(keyword || '')
    .trim()
    .toLowerCase()
  const words = search.split(/\s+/).filter(Boolean)

  if (search) {
    // 搜索模式：按匹配度筛选站点或包含匹配的企业/区域
    const matches = nodes.filter((node) => {
      return words.every((word) => node.searchText.includes(word))
    })

    const rank = (node) => {
      const lowerLabel = node.label.toLowerCase()
      if (lowerLabel === search) return 0
      if (lowerLabel.startsWith(search)) return 1
      if (lowerLabel.includes(search)) return 2
      return 3
    }

    // 优先站点，次级按匹配权重排序
    matches.sort((a, b) => {
      if (a.isSite !== b.isSite) {
        return a.isSite ? -1 : 1
      }
      const rankDiff = rank(a) - rank(b)
      if (rankDiff !== 0) return rankDiff
      return a.id - b.id
    })

    return matches.map((node) => node.id)
  }

  const collapsed = collapsedIds instanceof Set ? collapsedIds : new Set(collapsedIds || [])
  const visible = []
  const ids = []

  for (const node of nodes) {
    visible[node.id] = node.parentId === null || (visible[node.parentId] && !collapsed.has(node.parentId))
    if (visible[node.id]) {
      ids.push(node.id)
    }
  }

  return ids
}

/**
 * 计算虚拟滚动视口窗口数据
 * @param {Array} nodes 扁平节点全量
 * @param {Array} ids 当前可见行 ID 列表
 * @param {Array|Set} collapsedIds 折叠节点 ID 集合
 * @param {String} keyword 搜索词
 * @param {Number} scrollTop 当前滚动位
 * @param {Number} viewportHeight 视口高度
 * @param {Number} [rowHeightOverride] 自定义行高
 */
export function getSiteTreeWindow(
  nodes,
  ids,
  collapsedIds,
  keyword = '',
  scrollTop = 0,
  viewportHeight = 500,
  rowHeightOverride = null
) {
  if (!Array.isArray(nodes) || !Array.isArray(ids)) {
    return { rows: [], start: 0, end: 0, rowHeight: 36, total: 0, topSpace: 0, bottomSpace: 0 }
  }

  const hasSearch = Boolean(keyword && keyword.trim())
  const rowHeight = rowHeightOverride || (hasSearch ? 52 : 36)
  const visibleCount = Math.ceil(viewportHeight / rowHeight)
  const count = Math.min(100, visibleCount + 16)
  const buffer = Math.max(0, Math.floor((count - visibleCount) / 2))
  const start = Math.max(0, Math.min(Math.floor(scrollTop / rowHeight) - buffer, ids.length - count))
  const end = Math.min(ids.length, start + count)
  const collapsed = collapsedIds instanceof Set ? collapsedIds : new Set(collapsedIds || [])

  const rows = ids.slice(start, end).map((id) => {
    const row = sitePickerRow(nodes[id], keyword)
    if (row) {
      row.collapsed = collapsed.has(id)
    }
    return row
  }).filter(Boolean)

  return {
    rows,
    start,
    end,
    rowHeight,
    total: ids.length,
    topSpace: start * rowHeight,
    bottomSpace: Math.max(0, (ids.length - end) * rowHeight),
  }
}

/**
 * 提取顶层区域索引（用于区域速达）
 * @param {Array} nodes 扁平节点集合
 * @returns {Array} 区域列表 [{ id, key, label }]
 */
export function getSiteRegionIndex(nodes) {
  if (!Array.isArray(nodes)) return []
  const roots = nodes.filter(
    (node) => node.parentId === null && (node.key.startsWith('A_') || node.key.startsWith('R_'))
  )
  const targetRoots = roots.length ? roots : nodes.filter((node) => node.parentId === null && !node.isSite)
  // 单一顶级行政区/流域作为共同父级时，索引展示其直属下一级
  const children =
    targetRoots.length === 1
      ? nodes.filter((node) => node.parentId === targetRoots[0].id && !node.isSite)
      : []
  const target = children.length ? children : targetRoots
  return target.map((node) => ({ id: node.id, key: node.key, label: node.label }))
}

/**
 * 仅展开目标分组节点及其祖先（不递归展开其内部所有下级子分组）
 * @param {Array} nodes 扁平节点集合
 * @param {Array|Set} collapsedIds 折叠节点 ID 列表
 * @param {Number} id 目标节点 ID
 * @returns {Array} 更新后的折叠 ID 列表
 */
export function expandNodeOnly(nodes, collapsedIds, id) {
  if (!Array.isArray(nodes) || id == null || !nodes[id]) {
    return Array.isArray(collapsedIds) ? collapsedIds : Array.from(collapsedIds || [])
  }
  const toExpand = new Set([id])
  let parentId = nodes[id].parentId
  while (parentId !== null) {
    toExpand.add(parentId)
    parentId = nodes[parentId].parentId
  }
  const currentList = Array.isArray(collapsedIds) ? collapsedIds : Array.from(collapsedIds || [])
  return currentList.filter((item) => !toExpand.has(item))
}

/**
 * 展开目标节点分支及其所有祖先节点
 * @param {Array} nodes 扁平节点集合
 * @param {Array|Set} collapsedIds 折叠节点 ID 列表
 * @param {Number} id 目标节点 ID
 * @returns {Array} 更新后的折叠 ID 列表
 */
export function expandSiteBranch(nodes, collapsedIds, id) {
  if (!Array.isArray(nodes) || id == null || !nodes[id]) {
    return Array.isArray(collapsedIds) ? collapsedIds : Array.from(collapsedIds || [])
  }

  const targetNode = nodes[id]
  let end = id + 1
  while (end < nodes.length && nodes[end].level > targetNode.level) {
    end++
  }

  const ancestors = new Set()
  let parentId = targetNode.parentId
  while (parentId !== null) {
    ancestors.add(parentId)
    parentId = nodes[parentId].parentId
  }

  const currentList = Array.isArray(collapsedIds) ? collapsedIds : Array.from(collapsedIds || [])
  return currentList.filter((item) => (item < id || item >= end) && !ancestors.has(item))
}

/**
 * 折叠所有分组
 * @param {Array} nodes 扁平节点集合
 * @returns {Array} 全部非叶子节点的 ID 列表
 */
export function collapseAllGroups(nodes) {
  if (!Array.isArray(nodes)) return []
  return nodes.filter((node) => !node.isSite && node.hasChildren).map((node) => node.id)
}

/**
 * 展开所有分组
 * @returns {Array} 空折叠列表
 */
export function expandAllGroups() {
  return []
}

/**
 * 根据 key 查找节点
 * @param {Array} nodes 扁平节点集合
 * @param {String} key 节点 key
 * @returns {Object|null}
 */
export function findNodeByKey(nodes, key) {
  if (!Array.isArray(nodes) || !key) return null
  const targetKey = String(key)
  return (
    nodes.find(
      (node) =>
        String(node.key) === targetKey ||
        (node.data && String(node.data.id) === targetKey)
    ) || null
  )
}

/**
 * 获取某个节点的完整面包屑层级标签数组
 * @param {Array} nodes 扁平节点集合
 * @param {Object} node 当前节点
 * @returns {Array<string>}
 */
export function getNodeBreadcrumbs(nodes, node) {
  if (!Array.isArray(nodes) || !node) return []
  const names = []
  let current = node
  while (current) {
    if (!current.isSite) {
      names.unshift(current.label)
    }
    current = current.parentId !== null ? nodes[current.parentId] : null
  }
  return names
}
